/**
 * Loader + minimal typings for Stripe's Connect embedded-components SDK.
 *
 * The SDK ships CDN-only (there is no npm package for the plain HTML+JS build), so it is injected
 * as a script tag on first use. The load is memoised in a module-level promise, which is why this
 * lives in services/ rather than beside one feature: every Connect surface (disputes, the terminal
 * hardware shop) must share it. Two independent loaders would each see `window.StripeConnect`
 * still undefined during the other's download and inject a duplicate `connect.js`.
 *
 * @see https://docs.stripe.com/connect/get-started-connect-embedded-components
 */

export const STRIPE_CONNECT_SDK_URL = 'https://connect-js.stripe.com/v1.0/connect.js'

/** A load failure surfaced by an embedded component. */
export interface StripeConnectLoadError {
  error: { type: string; message?: string }
  elementTagName: string
}

/**
 * The custom element Connect returns from `create()`. Beyond the DOM API it carries per-component
 * setters; only the ones we use are typed here.
 */
export interface StripeConnectElement extends HTMLElement {
  setOnLoadError?: (handler: (loadError: StripeConnectLoadError) => void) => void
  setOnLoaderStart?: (handler: (event: { elementTagName: string }) => void) => void
  /** capital-financing: fires once the account's financing history has loaded. */
  setOnFinancingsLoaded?: (handler: (event: { total: number }) => void) => void
}

export interface StripeConnectInstance {
  create: (componentName: string) => StripeConnectElement
  update: (options: { appearance?: StripeConnectAppearance }) => void
}

/**
 * Styling for the embedded components. Connect renders them in an iframe our CSS cannot reach,
 * so these variables and `fonts` are the only styling channel.
 * @see https://docs.stripe.com/connect/embedded-appearance-options
 */
export interface StripeConnectAppearance {
  variables?: Record<string, string | number>
  overlays?: 'dialog' | 'drawer'
}

/** A webfont for Connect to load inside the iframe, so a custom fontFamily actually resolves. */
export interface StripeConnectFontSource {
  cssSrc: string
}

export interface StripeConnectInitOptions {
  publishableKey: string
  /** A resolved account-session secret. Connect cannot refresh it once it expires. */
  clientSecret?: string
  /** Preferred: lets Connect mint a fresh session itself when the current one expires. */
  fetchClientSecret?: () => Promise<string>
  appearance?: StripeConnectAppearance
  fonts?: StripeConnectFontSource[]
}

export interface StripeConnectGlobal {
  init: (options: StripeConnectInitOptions) => StripeConnectInstance
}

// connect.js attaches itself to window. Declaring it here — the one module that owns loading the
// SDK — types every reader of `window.StripeConnect` and removes the cast this used to need.
declare global {
  interface Window {
    StripeConnect?: StripeConnectGlobal
  }
}

const getStripeConnect = (): StripeConnectGlobal | undefined => window.StripeConnect

let sdkPromise: Promise<StripeConnectGlobal> | null = null

export const loadStripeConnectSdk = (): Promise<StripeConnectGlobal> => {
  if (sdkPromise) {
    return sdkPromise
  }

  sdkPromise = new Promise<StripeConnectGlobal>((resolve, reject) => {
    const existing = getStripeConnect()
    if (existing) {
      resolve(existing)
      return
    }

    const script = document.createElement('script')
    script.src = STRIPE_CONNECT_SDK_URL
    script.async = true
    script.onload = () => {
      const loaded = getStripeConnect()
      if (loaded) {
        resolve(loaded)
      } else {
        reject(new Error('Stripe Connect SDK loaded without exposing StripeConnect'))
      }
    }
    script.onerror = () => reject(new Error('Failed to load the Stripe Connect SDK'))
    document.body.appendChild(script)
  })

  // A failed load must not poison every later attempt — drop the memo so a retry re-injects.
  sdkPromise.catch(() => {
    sdkPromise = null
  })

  return sdkPromise
}
